import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import FormInput from './FormInput';
import SignupOptionsField from './SignupOptionsField';
import RecurrenceField, { EMPTY_RECURRENCE, RecurrenceValue } from './RecurrenceField';

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventAdded: () => void;
}

const AddEventModal: React.FC<AddEventModalProps> = ({ isOpen, onClose, onEventAdded }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    description: '',
    category: 'Bible Study',
    category_other: '',
    requester_name: '',
    requester_email: '',
    allow_volunteers: true,
    allow_attendees: false,
  });

  const [recurrence, setRecurrence] = useState<RecurrenceValue>(EMPTY_RECURRENCE);

  const modalRef = useRef<HTMLDivElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      initialFocusRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { error } = await supabase
        .from('events')
        .insert([{
          title: formData.title,
          date: formData.date,
          time: formData.time,
          location: formData.location,
          description: formData.description,
          category: formData.category === 'Other'
            ? `Other: ${formData.category_other.trim()}`
            : formData.category,
          requester_name: formData.requester_name,
          requester_email: formData.requester_email,
          allow_volunteers: formData.allow_volunteers,
          allow_attendees: formData.allow_attendees,
          is_recurring: recurrence.is_recurring,
          recurrence_frequency: recurrence.recurrence_frequency,
          recurrence_end_date: recurrence.recurrence_end_date || null,
          status: 'pending'
        }]);

      if (error) throw error;

      toast.success('Event request submitted successfully! It will be reviewed soon.');
      onEventAdded();
      onClose();
      setFormData({
        title: '',
        date: '',
        time: '',
        location: '',
        description: '',
        category: 'Bible Study',
        category_other: '',
        requester_name: '',
        requester_email: '',
        allow_volunteers: true,
        allow_attendees: false,
      });
      setRecurrence(EMPTY_RECURRENCE);
    } catch (error) {
      console.error('Error adding event:', error);
      toast.error('Failed to submit event request');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        role="document"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 id="modal-title" className="text-2xl font-bold text-gray-900">Request New Event</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            ref={initialFocusRef}
            label="Event Title"
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Enter a clear, descriptive title"
            maxLength={100}
            aria-required="true"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Date"
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              min={new Date().toISOString().split('T')[0]}
              aria-required="true"
            />

            <FormInput
              label="Time"
              type="time"
              required
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              aria-required="true"
            />
          </div>

          <RecurrenceField
            startDate={formData.date}
            time={formData.time}
            value={recurrence}
            onChange={setRecurrence}
          />

          <FormInput
            label="Location"
            type="text"
            required
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="Physical address or virtual meeting link"
            aria-required="true"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="category">
              Category
            </label>
            <select
              id="category"
              required
              className="w-full px-4 py-2 border rounded-md"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              aria-required="true"
            >
              <option value="Bible Study">Bible Study</option>
              <option value="Workshop">Workshop</option>
              <option value="Service">Service</option>
              <option value="Fellowship">Fellowship</option>
              <option value="Other">Other</option>
            </select>
            {formData.category === 'Other' && (
              <div className="mt-3 animate-in fade-in">
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="category_other">
                  Please describe the category
                </label>
                <input
                  id="category_other"
                  type="text"
                  required
                  maxLength={40}
                  className="w-full px-4 py-2 border rounded-md"
                  value={formData.category_other}
                  onChange={(e) => setFormData({ ...formData, category_other: e.target.value })}
                  placeholder="e.g., Men's Retreat, Sports Night"
                  aria-required="true"
                  autoFocus
                />
                <p className="text-sm text-gray-500 mt-1">{formData.category_other.length}/40 characters</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="description">
              Description (250 characters max)
            </label>
            <textarea
              id="description"
              required
              maxLength={250}
              rows={4}
              className="w-full px-4 py-2 border rounded-md"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Please provide a brief overview of the event"
              aria-required="true"
            />
            <p className="text-sm text-gray-500 mt-1" aria-live="polite">
              {formData.description.length}/250 characters
            </p>
          </div>

          <SignupOptionsField
            allowVolunteers={formData.allow_volunteers}
            allowAttendees={formData.allow_attendees}
            onChange={(next) => setFormData({ ...formData, ...next })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Your Name"
              type="text"
              required
              value={formData.requester_name}
              onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
              placeholder="Your full name"
              aria-required="true"
            />

            <FormInput
              label="Your Email"
              type="email"
              required
              value={formData.requester_email}
              onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
              placeholder="Your email address"
              aria-required="true"
            />
          </div>

          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Submit Event Request
            </button>
          </div>

          <p className="text-sm text-gray-500 mt-4">
            * Required fields. All submissions will be reviewed and you will receive a confirmation email once your event has been approved.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AddEventModal;